import React, { useMemo } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  responseMilestones,
  classifyResponse,
  mrDepthData,
  MMR_THRESHOLD,
} from '../../data/diseasesData';
import { ResponseCategory, Tone } from '../../types';
import { useUserInput } from '../../context/UserInputContext';
import CalcShell from '../../components/CalcShell';
import NumberField from '../../components/NumberField';

const CATEGORY_TONE: Record<ResponseCategory, Tone> = {
  favorable: 'green',
  warning: 'yellow',
  unfavorable: 'red',
};

const CATEGORY_LABEL: Record<ResponseCategory, string> = {
  favorable: 'Favorable',
  warning: 'Warning',
  unfavorable: 'Unfavorable',
};

/** Deepest molecular-response level the value reaches, if any. */
const getMatchingMrDepth = (bcrAbl: number) => {
  let match: (typeof mrDepthData)[number] | null = null;
  for (const row of mrDepthData) {
    if (bcrAbl <= row.thresholdValue) match = row;
  }
  return match;
};

const TreatmentResponse: React.FC = () => {
  const { t } = useTranslation('hematology');
  const { state, update } = useUserInput();
  const { timePoint, bcrAblInput } = state.treatmentResponse;

  const setTimePoint = (v: string) =>
    update('treatmentResponse', { ...state.treatmentResponse, timePoint: v });
  const setBcrAblInput = (v: string) =>
    update('treatmentResponse', { ...state.treatmentResponse, bcrAblInput: v });

  const TIME_POINT_OPTIONS = useMemo(() => [
    { value: '', label: t('response.selectTimePoint') },
    { value: '3 months', label: t('response.3months') },
    { value: '6 months', label: t('response.6months') },
    { value: '12 months', label: t('response.12months') },
    { value: 'At any time', label: t('response.atAnyTime') },
  ], [t]);

  const CATEGORY_LABEL_HE: Record<ResponseCategory, string> = useMemo(() => ({
    favorable: t('response.goodResponse'),
    warning: t('response.borderlineResponse'),
    unfavorable: t('response.inadequateResponse'),
  }), [t]);

  const bcrAblValue = useMemo(() => {
    if (bcrAblInput.trim() === '') return null;
    const val = parseFloat(bcrAblInput);
    return isNaN(val) || val < 0 ? null : val;
  }, [bcrAblInput]);

  const classification = useMemo(() => {
    if (!timePoint || bcrAblValue === null) return null;
    return classifyResponse(timePoint, bcrAblValue);
  }, [timePoint, bcrAblValue]);

  const mrMatch = useMemo(
    () => (bcrAblValue === null ? null : getMatchingMrDepth(bcrAblValue)),
    [bcrAblValue]
  );

  const isMmr = bcrAblValue !== null && bcrAblValue <= MMR_THRESHOLD;

  const isCellHighlighted = (rowTimePoint: string, column: ResponseCategory) =>
    rowTimePoint === timePoint && classification?.category === column;

  const inputs = (
    <>
      <NumberField
        label={
          <>
            BCR::ABL1<sup>IS</sup>
          </>
        }
        value={bcrAblInput}
        onChange={setBcrAblInput}
        placeholder={t('response.bcrAblPlaceholder')}
        hint={t('response.bcrAblHint')}
        min={0}
        step={0.001}
        decimal
      />

      <div className="hema-input-group">
        <label className="hema-label" htmlFor="response-timepoint">
          {t('response.timePointLabel')}
        </label>
        <div className="hema-field">
          <select
            id="response-timepoint"
            value={timePoint}
            onChange={(e) => setTimePoint(e.target.value)}
          >
            {TIME_POINT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </>
  );

  const summary =
    bcrAblValue === null ? null : (
      <div className="response-summary">
        <div className="response-summary__value">
          <span className="response-summary__value-label">
            BCR::ABL1<sup>IS</sup>
          </span>
          <span className="response-summary__value-number" dir="ltr">
            {bcrAblValue}%
          </span>
        </div>

        <div className="response-summary__badges">
          {mrMatch ? (
            <>
              <span className="depth-chip depth-chip--primary" dir="ltr">
                {mrMatch.abbreviation}
              </span>
              {isMmr && (
                <span className="depth-chip depth-chip--mmr" dir="ltr">
                  <Check size={14} strokeWidth={3} />
                  MMR — Major Molecular Response
                </span>
              )}
            </>
          ) : (
            <span className="depth-chip depth-chip--none">
              {t('response.aboveCcyr')}
            </span>
          )}
        </div>

        {timePoint && classification && (
          <div
            className={`response-verdict tone--${CATEGORY_TONE[classification.category]}`}
          >
            <div className="response-verdict__head">
              <span className="response-verdict__timepoint">
                {TIME_POINT_OPTIONS.find((o) => o.value === timePoint)?.label}
              </span>
              <span className="response-verdict__category">
                {CATEGORY_LABEL[classification.category]} ·{' '}
                {CATEGORY_LABEL_HE[classification.category]}
              </span>
            </div>
            {classification.noteKey && (
              <p className="response-verdict__note">{t(classification.noteKey)}</p>
            )}
          </div>
        )}

      </div>
    );

  return (
    <CalcShell
      title={t('response.title')}
      subtitle={t('response.subtitle')}
      inputs={inputs}
      aside={summary}
    >
      {/* ── Response milestones ── */}
      <section className="ref-block">
        <h4 className="ref-block__title">
          Response Milestones — 1<sup>st</sup>, 2<sup>nd</sup> and 3<sup>rd</sup>{' '}
          line TKI
        </h4>

        <div className="gtable gtable--response" dir="ltr">
          <div className="gtable__head">
            <span className="gtable__col-head" />
            <span className="gtable__col-head tone--green">Favorable</span>
            <span className="gtable__col-head tone--yellow">Warning</span>
            <span className="gtable__col-head tone--red">Unfavorable</span>
          </div>

          {responseMilestones.map((row) => (
            <div
              key={row.timePoint}
              className={`gtable__row ${row.timePoint === timePoint ? 'is-selected' : ''}`}
            >
              <div className="gtable__rowhead">{row.timePoint}</div>

              <div
                className={`gtable__cell tone--green ${isCellHighlighted(row.timePoint, 'favorable') ? 'is-highlighted' : ''}`}
                data-label="Favorable"
              >
                {row.favorable}
              </div>
              <div
                className={`gtable__cell tone--yellow ${isCellHighlighted(row.timePoint, 'warning') ? 'is-highlighted' : ''}`}
                data-label="Warning"
              >
                {row.warning}
              </div>
              <div
                className={`gtable__cell tone--red ${isCellHighlighted(row.timePoint, 'unfavorable') ? 'is-highlighted' : ''}`}
                data-label="Unfavorable"
              >
                {row.unfavorable}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Molecular response depth ── */}
      <section className="ref-block">
        <h4 className="ref-block__title">{t('response.mrDepthTitle')}</h4>

        <div className="gtable gtable--mr" dir="ltr">
          <div className="gtable__head">
            <span className="gtable__col-head">Terminology</span>
            <span className="gtable__col-head">Abbreviation</span>
            <span className="gtable__col-head">
              BCR::ABL1<sup>IS</sup>
            </span>
            <span className="gtable__col-head">Min. ABL1 transcripts</span>
          </div>

          {mrDepthData.map((row) => (
            <div
              key={row.abbreviation}
              className={`gtable__row ${mrMatch?.abbreviation === row.abbreviation ? 'is-selected' : ''}`}
            >
              <div className="gtable__rowhead gtable__rowhead--wide">
                {row.terminology}
              </div>
              <div className="gtable__cell is-strong" data-label="Abbreviation">
                {row.abbreviation}
              </div>
              <div className="gtable__cell is-mono" data-label="BCR::ABL1 IS">
                {row.bcrAblThreshold}
              </div>
              <div className="gtable__cell" data-label="Min. ABL1 transcripts">
                {row.minAbl1Transcripts}
              </div>
            </div>
          ))}
        </div>
      </section>
    </CalcShell>
  );
};

export default TreatmentResponse;
